import type { ErrorCode } from '@/locales/en/errorMessages';
import { isAppError } from './assertions';
import { useLocale } from '@/composables/locale';
import { toValue } from 'vue';
import errorMessages from '@/locales/en/errorMessages';
type ErrorMessageVars = Record<string, string>;

/**
 * Parse backend message to extract code and variables
 * Examples:
 * - "auth_failed" -> { code: "auth_failed", vars: {} }
 * - "incorrect_otp_VARS:{"attempts":2}" -> { code: "incorrect_otp", vars: { attempts: 2 } }
 */
export function parseMessageWithVars(message: string): {
    code: string;
    vars: ErrorMessageVars;
} {
    const [code, varsJson] = message.split('__VARS:');
    const vars = varsJson ? JSON.parse(varsJson) : {};
    return { code, vars };
}

export function isErrorOfCode(error: unknown, type: ErrorCode): boolean {
    if (!isAppError(error)) return false;
    return error.body.message === type;
}

export function isKnownError(error: unknown): boolean {
    if (!isAppError(error)) return false;
    const { code } = parseMessageWithVars(error.body.message);
    return code in errorMessages;
}

export function parseError(error: unknown, fallback?: string): string {
    const { t } = useLocale();
    const fallbackMessage =
        fallback || t('generic.common_terms.something_went_wrong');
    const errorValue = toValue(error);
    if (!isAppError(errorValue) || !isKnownError(errorValue)) {
        return fallbackMessage;
    }
    const { code, vars } = parseMessageWithVars(errorValue.body.message);
    const translationKey = `errorMessages.${code}`;

    return t(translationKey, vars);
}

export function isAportError(error: unknown): boolean {
    return error instanceof Error && error.name === 'AportError';
}

export function isNotFoundError(error: unknown): boolean {
    if (!isAppError(error)) return false;
    return error.cause?.status === 404;
}
